import { useEffect, useRef, useState } from "react";
import { useBlocker, useRouterState } from "@tanstack/react-router";

import {
  CONFIDENTIAL_ROUTE_ID,
  useConfidentialSessionStore,
} from "@/atoms/confidential-session";
import { useMessagesStore } from "@/atoms/playground";
import { ConfidentialLeaveDialog } from "@/pages/Playground/components/confidential-leave-dialog";
import {
  closeConfidentialSession,
  getConfidentialSessionErrorMessage,
} from "@/services/confidential-service";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";
import { getPostLeaveExternalUrl } from "@/utils/confidential-mode";

type HistoryMethod = "pushState" | "replaceState";

type PendingLegacyNavigation = {
  args: Parameters<History[HistoryMethod]>;
  navigate: History[HistoryMethod];
};

type PendingExternalNavigation = {
  href: string;
  target: string;
};

type ExternalOpener = { element: HTMLAnchorElement; pathname: string; sessionId: string | undefined };

const isEligibleOpener = (element: HTMLAnchorElement) => {
  if (!element.isConnected || element.ownerDocument !== document ||
    element.matches(":disabled, [disabled], [aria-disabled='true']") ||
    element.closest("[hidden], [inert], [aria-hidden='true'], [aria-disabled='true']")) return false;
  for (let node: HTMLElement | null = element; node; node = node.parentElement) {
    const style = getComputedStyle(node);
    if (style.display === "none" || style.visibility === "hidden" || style.visibility === "collapse" ||
      style.contentVisibility === "hidden" || style.opacity === "0") return false;
  }
  return Array.from(element.getClientRects()).some(rect => rect.width > 0 && rect.height > 0);
};

const isConfidentialPath = (pathname: string) =>
  pathname.endsWith(`/${CONFIDENTIAL_ROUTE_ID}`);

const isStandaloneConfidentialPath = (pathname: string) =>
  pathname.startsWith("/chat-standalone/") && isConfidentialPath(pathname);

const isDifferentPage = (url: string | URL | null | undefined) => {
  if (url == null) return false;
  return (
    new URL(String(url), window.location.href).pathname !==
    window.location.pathname
  );
};

export function ConfidentialNavigationGuard() {
  const { t } = useTranslation();
  const pathname = useRouterState({
    select: (state) => state.location.pathname,
  });
  const session = useConfidentialSessionStore((state) => state.session);
  const status = useConfidentialSessionStore((state) => state.status);
  const clearConfidentialSession = useConfidentialSessionStore(
    (state) => state.clear,
  );
  const setMessages = useMessagesStore((state) => state.setMessages);
  const setReferences = useMessagesStore((state) => state.setReferences);
  const pendingLegacyNavigationRef =
    useRef<PendingLegacyNavigation | null>(null);
  const pendingExternalNavigationRef =
    useRef<PendingExternalNavigation | null>(null);
  const [hasPendingLegacyNavigation, setHasPendingLegacyNavigation] =
    useState(false);
  const [hasPendingExternalNavigation, setHasPendingExternalNavigation] =
    useState(false);
  const [isLeaving, setIsLeaving] = useState(false);
  const externalOpenerRef = useRef<ExternalOpener | null>(null);
  const cancelledOpenerRef = useRef<ExternalOpener | null>(null);
  const mounted = useRef(false);
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
      externalOpenerRef.current = null;
      cancelledOpenerRef.current = null;
    };
  }, []);
  const isConfidentialActive = status === "active" && Boolean(session);
  const shouldGuardConfidentialNavigation =
    isConfidentialPath(pathname) &&
    status !== "expired" &&
    (isConfidentialActive || isStandaloneConfidentialPath(pathname));
  const navigationBlocker = useBlocker({
    shouldBlockFn: ({ current, next }) =>
      shouldGuardConfidentialNavigation &&
      isConfidentialPath(current.pathname) &&
      current.pathname !== next.pathname,
    enableBeforeUnload: false,
    disabled: !shouldGuardConfidentialNavigation,
    withResolver: true,
  });

  useEffect(() => {
    if (!shouldGuardConfidentialNavigation) return;

    const originalPushState = window.history.pushState.bind(window.history);
    const originalReplaceState = window.history.replaceState.bind(
      window.history,
    );

    const intercept = (
      navigate: History[HistoryMethod],
      args: Parameters<History[HistoryMethod]>,
    ) => {
      if (
        isConfidentialPath(window.location.pathname) &&
        isDifferentPage(args[2])
      ) {
        pendingLegacyNavigationRef.current = { args, navigate };
        setHasPendingLegacyNavigation(true);
        return;
      }
      navigate(...args);
    };

    const guardedPushState: History["pushState"] = (...args) =>
      intercept(originalPushState, args);
    const guardedReplaceState: History["replaceState"] = (...args) =>
      intercept(originalReplaceState, args);

    window.history.pushState = guardedPushState;
    window.history.replaceState = guardedReplaceState;

    return () => {
      if (window.history.pushState === guardedPushState) {
        window.history.pushState = originalPushState;
      }
      if (window.history.replaceState === guardedReplaceState) {
        window.history.replaceState = originalReplaceState;
      }
    };
  }, [shouldGuardConfidentialNavigation]);

  useEffect(() => {
    if (!shouldGuardConfidentialNavigation) return;

    const handleGuardedExternalNavigation = (event: MouseEvent) => {
      if (event.button !== 0 || !(event.target instanceof Element)) return;

      const link = event.target.closest<HTMLAnchorElement>(
        'a[data-confidential-leave-guard="true"]',
      );
      if (!link) return;

      event.preventDefault();
      externalOpenerRef.current = { element: link, pathname: window.location.pathname, sessionId: session?.id };
      cancelledOpenerRef.current = null;
      pendingExternalNavigationRef.current = {
        href: link.href,
        target: link.target || "_self",
      };
      setHasPendingExternalNavigation(true);
    };

    document.addEventListener("click", handleGuardedExternalNavigation, true);
    return () =>
      document.removeEventListener(
        "click",
        handleGuardedExternalNavigation,
        true,
      );
  }, [shouldGuardConfidentialNavigation, session?.id]);

  useEffect(() => {
    if (!shouldGuardConfidentialNavigation) return;

    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      event.returnValue = "";
    };

    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => window.removeEventListener("beforeunload", handleBeforeUnload);
  }, [shouldGuardConfidentialNavigation]);

  const clearSessionUi = () => {
    clearConfidentialSession();
    setMessages([]);
    setReferences({});
  };

  const handleLeave = async () => {
    if (isLeaving) return;

    const pendingLegacyNavigation = pendingLegacyNavigationRef.current;
    const pendingExternalNavigation = pendingExternalNavigationRef.current;
    setIsLeaving(true);

    try {
      if (session?.id) {
        await closeConfidentialSession(session.id);
      }
    } catch (error) {
      toast.error(
        getConfidentialSessionErrorMessage(
          error,
          t("playground.failedToCloseConfidentialSession"),
        ),
      );
      return;
    } finally {
      setIsLeaving(false);
    }

    // Successful navigation owns focus; only explicit cancellation may restore it.
    externalOpenerRef.current = null;
    cancelledOpenerRef.current = null;
    clearSessionUi();

    if (navigationBlocker.status === "blocked") {
      navigationBlocker.proceed();
      return;
    }

    if (pendingExternalNavigation) {
      pendingExternalNavigationRef.current = null;
      setHasPendingExternalNavigation(false);
      window.open(
        getPostLeaveExternalUrl(pendingExternalNavigation.href),
        pendingExternalNavigation.target,
        "noopener,noreferrer",
      );
      return;
    }

    if (!pendingLegacyNavigation) return;

    pendingLegacyNavigationRef.current = null;
    setHasPendingLegacyNavigation(false);
    pendingLegacyNavigation.navigate(...pendingLegacyNavigation.args);
    window.dispatchEvent(
      new PopStateEvent("popstate", {
        state: pendingLegacyNavigation.args[0],
      }),
    );
  };

  const handleOpenChange = (open: boolean) => {
    if (open) return;
    if (isLeaving) return;

    cancelledOpenerRef.current = externalOpenerRef.current;
    externalOpenerRef.current = null;

    if (navigationBlocker.status === "blocked") {
      navigationBlocker.reset();
    }
    pendingLegacyNavigationRef.current = null;
    pendingExternalNavigationRef.current = null;
    setHasPendingLegacyNavigation(false);
    setHasPendingExternalNavigation(false);
  };

  return (
    <ConfidentialLeaveDialog
      open={
        hasPendingLegacyNavigation ||
        hasPendingExternalNavigation ||
        navigationBlocker.status === "blocked"
      }
      onOpenChange={handleOpenChange}
      onLeave={handleLeave}
      isLeaving={isLeaving}
      onCloseAutoFocus={event => {
        // This controlled dialog has no Radix Trigger. Restore after its close
        // lifecycle, without overriding another navigation or focus owner.
        event.preventDefault();
        const opener = cancelledOpenerRef.current;
        cancelledOpenerRef.current = null;
        if (!mounted.current || !opener || externalOpenerRef.current ||
          window.location.pathname !== opener.pathname || session?.id !== opener.sessionId) return;
        const focused = document.activeElement;
        if (focused !== document.body && focused !== opener.element &&
          !(event.target instanceof HTMLElement && focused && event.target.contains(focused))) return;
        if (isEligibleOpener(opener.element)) opener.element.focus({ preventScroll: true });
      }}
    />
  );
}
